import { Link } from 'react-router-dom';
import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/subscribe`, { email });
      toast.success(res.data.message);
      setEmail('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? 'Please enter a valid email' : (detail || 'Something went wrong'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#0a0a0a] border-t border-white/5 py-16 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          {/* Brand + Subscribe */}
          <div className="md:col-span-2">
            <h3 className="text-2xl font-serif italic text-white mb-4">meet mr.fashion</h3>
            <p className="text-gray-500 text-sm leading-relaxed max-w-md mb-8">
              A social network dedicated to fashion. Connect with style enthusiasts,
              discover luxury brands, and share your unique fashion perspective with the world.
            </p>

            {/* Subscribe */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-3">Stay in the loop</p>
              <form onSubmit={handleSubscribe} className="flex gap-0 max-w-sm" data-testid="footer-subscribe-form">
                <input type="text" name="honeypot" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="flex-1 bg-[#1c1c1c] border border-white/10 border-r-0 text-white text-sm px-4 py-3 placeholder:text-gray-600 focus:outline-none focus:border-[#C41E3A]/50 transition-colors"
                  data-testid="footer-subscribe-email"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-5 py-3 text-xs uppercase tracking-wider font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                  data-testid="footer-subscribe-btn"
                >
                  {submitting ? '...' : (
                    <>
                      Subscribe
                      <ArrowRight className="w-3.5 h-3.5" />
                    </>
                  )}
                </button>
              </form>
              <p className="text-[10px] text-gray-600 mt-2">Get HEIR drops and style news. Unsubscribe anytime.</p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-6">Explore</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/feed" className="text-gray-500 hover:text-white text-sm transition-colors">
                  Fashion Feed
                </Link>
              </li>
              <li>
                <Link to="/brands" className="text-gray-500 hover:text-white text-sm transition-colors">
                  Luxury Brands
                </Link>
              </li>
              <li>
                <Link to="/challenges" className="text-gray-500 hover:text-white text-sm transition-colors">
                  Style Challenges
                </Link>
              </li>
              <li>
                <Link to="/one-brand-week" className="text-gray-500 hover:text-white text-sm transition-colors">
                  One Brand Week
                </Link>
              </li>
              <li>
                <Link to="/heir" className="text-gray-500 hover:text-white text-sm transition-colors">
                  HEIR&reg;
                </Link>
              </li>
              <li>
                <Link to="/book" className="text-gray-500 hover:text-white text-sm transition-colors">
                  The Art of Luxury Fashion
                </Link>
              </li>
            </ul>
          </div>

          {/* Featured Brands */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-6">Fashion Icons</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/brands/louis-vuitton" className="text-gray-500 hover:text-white text-sm transition-colors">
                  Louis Vuitton
                </Link>
              </li>
              <li>
                <Link to="/brands/prada" className="text-gray-500 hover:text-white text-sm transition-colors">
                  Prada
                </Link>
              </li>
              <li>
                <Link to="/brands/gucci" className="text-gray-500 hover:text-white text-sm transition-colors">
                  Gucci
                </Link>
              </li>
              <li>
                <Link to="/brands/hermes" className="text-gray-500 hover:text-white text-sm transition-colors">
                  Hermes
                </Link>
              </li>
              <li>
                <Link to="/brands/chanel" className="text-gray-500 hover:text-white text-sm transition-colors">
                  Chanel
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-600 text-xs tracking-wider">
            &copy; {new Date().getFullYear()} Meet Mr. Fashion. All rights reserved.
          </p>
          <p className="text-gray-600 text-xs tracking-wider">
            Featuring: The Art of Luxury Fashion by Akeem Feiton
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
